import { useState, useEffect, useRef } from 'react'
import { gql } from 'graphql-request'
import { useForm, useFieldArray } from 'react-hook-form'
import QRCode from 'react-qr-code'
import { useMutation } from '@tanstack/react-query'

import { request } from '@helpers/graphql'
import { RefreshCw as RefreshCwIcon, QrCode as QrCodeIcon, Loader2 as CircleSpinner } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CopyToClipboardInput from '@components/CopyToClipboardInput'
import DismissiblePill from '@components/DismissiblePill'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useFlashMessage } from '@components/FlashMessage'

const INVITE_EDUCATORS_TO_ORGANIZATION_MUTATION = gql`
  mutation adminInviteEducatorsToOrganization(
    $adminInviteEducatorsToOrganizationInput: AdminInviteEducatorsToOrganizationInput!
  ) {
    adminInviteEducatorsToOrganization(
      input: $adminInviteEducatorsToOrganizationInput
    ) {
      success
      invalidEmailAddresses
    }
  }
`

const CREATE_OR_GET_ORGANIZATION_JOIN_CODE_MUTATION = gql`
  mutation createOrGetOrganizationJoinCode($input: CreateOrGetOrganizationJoinCodeInput!) {
    createOrGetOrganizationJoinCode(input: $input) {
      organizationJoinCode {
        token
      }
      success
      errors
    }
  }
`

const RESET_ORGANIZATION_JOIN_CODE_MUTATION = gql`
  mutation resetOrganizationJoinCode($input: ResetOrganizationJoinCodeInput!) {
    resetOrganizationJoinCode(input: $input) {
      organizationJoinCode {
        token
      }
      success
    }
  }
`

const isValidEmail = email => /^\S+@\S+\.\S+$/.test(email)

const InviteEducatorsModal = ({ organizationId, closeModal, isOpen }) => {
  const [invitationToken, setInvitationToken] = useState('')
  const qrCodeRef = useRef()
  const emailInputRef = useRef(null)

  const { setFlashMessage } = useFlashMessage()
  const { handleSubmit, reset, setError, clearErrors, control, formState: { errors }, getValues } = useForm({
    mode: 'onTouched',
    defaultValues: {
      emailAddresses: []
    }
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'emailAddresses'
  })

  const { mutateAsync: inviteEducators, isLoading: sending } = useMutation({
    mutationFn: async (variables) => request(INVITE_EDUCATORS_TO_ORGANIZATION_MUTATION, { adminInviteEducatorsToOrganizationInput: variables }),
    onSettled: (response) => {
      const data = response?.adminInviteEducatorsToOrganization

      if (data.success === true) {
        setFlashMessage('Invitations sent', 'success')
        reset()
        closeModal()
      } else {
        const message = data.invalidEmailAddresses.length > 0
          ? 'Some of the email addresses listed are already members of this organization.'
          : 'Something went wrong'

        setError('general', { message })

        data.invalidEmailAddresses.forEach(invalidEmail => {
          const fieldIndex = fields.findIndex(field => field.email === invalidEmail)
          if (fieldIndex !== -1) {
            setError(`emailAddresses.${fieldIndex}`, { message: 'Invalid email' })
          }
        })
      }
    }
  })

  const addEmailAddress = email => {
    if (!fields.some(field => field.email === email)) {
      append({ email })
    }
    if (emailInputRef.current) {
      emailInputRef.current.value = ''
    }
  }

  const handlePaste = (e) => {
    clearErrors('general')

    const value = e.clipboardData.getData('text')
    const emails = value.split(/[,;\s]+/).map(email => email.trim()).filter(Boolean)

    if (emails.length > 0) {
      e.preventDefault() // Prevent the default paste
      emails.forEach(email => {
        if (isValidEmail(email)) {
          addEmailAddress(email)
        }
      })
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',' || e.key === ';') {
      e.preventDefault()
      const value = e.target.value.trim()
      if (isValidEmail(value)) {
        addEmailAddress(value)
      } else if (value !== '') {
        // Optionally, provide feedback for invalid email entered directly
        // For now, just doesn't add it if not valid
      }
    }
  }

  const handleBlur = (e) => {
    const value = e.target.value.trim()
    if (value && isValidEmail(value)) {
      addEmailAddress(value)
    }
  }

  const actualSubmitLogic = () => {
    clearErrors('general'); // Clear only our specific error
    const currentFormValues = getValues();
    const emailListFromForm = currentFormValues.emailAddresses || [];

    if (emailListFromForm.length === 0) {
      setError('general', { message: 'Email addresses are required' });
      return;
    }
    inviteEducators({ organizationId, emailAddresses: emailListFromForm.map(field => field.email) });
  };

  const { mutateAsync: createOrGetOrganizationJoinCode } = useMutation({
    mutationFn: async () => request(CREATE_OR_GET_ORGANIZATION_JOIN_CODE_MUTATION, { input: { organizationId } })
  })

  const { mutateAsync: resetOrganizationJoinCode, isLoading: isResettingInvitationLink } = useMutation({
    mutationFn: async () => request(RESET_ORGANIZATION_JOIN_CODE_MUTATION, { input: { organizationId } }),
    onSuccess: (data) => {
      const token = data?.resetOrganizationJoinCode?.organizationJoinCode?.token
      if (token) {
        setInvitationToken(token)
      } else {
        setFlashMessage('Failed to reset invitation link.', 'error')
      }
    },
    onError: (error) => {
      setFlashMessage(error.message || 'Failed to reset invitation link.', 'error')
    }
  })

  const resetInvitationToken = async () => {
    await resetOrganizationJoinCode()
  }

  useEffect(() => {
    const fetchToken = async () => {
      if (isOpen) {
        const data = await createOrGetOrganizationJoinCode()
        const { createOrGetOrganizationJoinCode: { organizationJoinCode: { token } = {}, success } } = data

        if (success) setInvitationToken(token)
      }
    }

    fetchToken()
  }, [isOpen])

  const inviteLink = `${window.location.origin}/educator_accept_invitation?invitation_token=${invitationToken}`

  const downloadQRCode = () => {
    const svg = qrCodeRef.current
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    const padding = 20

    img.onload = () => {
      canvas.width = img.width + (padding * 2)
      canvas.height = img.height + (padding * 2)

      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.drawImage(img, padding, padding)

      const pngFile = canvas.toDataURL('image/png')
      const downloadLink = document.createElement('a')
      downloadLink.download = 'invitation-qr-code.png'
      downloadLink.href = pngFile
      downloadLink.click()
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Invite educators</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(actualSubmitLogic)} className='flex flex-col space-y-4'>
          <div>
            <div className='flex gap-2 mt-5 mb-3 items-end'>
              <CopyToClipboardInput
                value={inviteLink}
                label='Invite link'
                className='w-full'
              />

              <QRCode
                className='hidden'
                ref={qrCodeRef}
                value={inviteLink}
                size={256}
              />

              <Button
                variant='outline'
                type='button'
                onClick={downloadQRCode}
                aria-label="Download QR code"
                className="p-2 h-auto"
              >
                <QrCodeIcon className='size-5' />
              </Button>

              <Button
                variant='outline'
                type='button'
                onClick={resetInvitationToken}
                disabled={isResettingInvitationLink}
                aria-label="Reset invitation link"
                className="p-2 h-auto"
              >
                {isResettingInvitationLink ? (
                  <CircleSpinner className='size-5 animate-spin' />
                ) : (
                  <RefreshCwIcon className='size-5' />
                )}
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor='email-input'>Email Addresses</Label>
            <p className='text-sm text-muted-foreground mt-0 mb-3'>Add multiple email addresses by separating them with commas, spaces, or semicolons. Press Enter, space, or comma to add.</p>

            <div className='flex flex-wrap gap-2 mb-2'>
              <For each='field' index='index' of={fields}>
                <DismissiblePill
                  key={field.id}
                  label={field.email}
                  onDismiss={() => remove(index)}
                  className={errors.emailAddresses?.[index] ? 'border border-destructive' : ''}
                />
              </For>
            </div>

            <Textarea
              id='email-input'
              ref={emailInputRef}
              placeholder='Enter email address and press Enter, space, or comma'
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className='w-full h-30'
            />
            {errors.general && <p className='text-sm text-destructive mt-1'>{errors.general.message}</p>}
          </div>

          <DialogFooter className='sm:justify-between'>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type='button'
              onClick={() => {
                const textInputValue = emailInputRef.current?.value.trim();
                if (textInputValue && isValidEmail(textInputValue)) {
                  addEmailAddress(textInputValue);
                }
                setTimeout(() => {
                  handleSubmit(actualSubmitLogic)();
                }, 0);
              }}
              disabled={sending}
              loading={sending}
            >
              {sending ? 'Sending...' : 'Send invitations'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default InviteEducatorsModal
